import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import ListActions from 'Components/ListActions'
import { WithLoader } from 'Components/WithLoader'
import CodespaceDetails from 'Services/ControlDataManagement/Features/Codespace/Components/CodespaceDetails'
import CodespaceList from 'Services/ControlDataManagement/Features/Codespace/Components/CodespaceList'
import {
  useDeleteSingleCodespaceMutation,
  useGetSingleCodespaceQuery
} from 'Services/ControlDataManagement/Features/Codespace/codespaceApi'
import { useCodespacesWithParams } from 'Services/ControlDataManagement/Features/Codespace/Hooks'
import { CodespaceTabType } from 'Services/ControlDataManagement/Features/Codespace/types'
import {
  CONTROL_DATA_MANAGEMENT_ROOT_URL,
  ROWS_PER_PAGE_DEFAULT,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC
} from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider, Tab, Typography } from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

export default function CodespaceListPage() {
  const baseUrl = `${CONTROL_DATA_MANAGEMENT_ROOT_URL}/codespaces`
  const navigate = useNavigate()
  const { confirm } = useConfirm()

  const {
    codespaces,
    isFetching,
    params,
    setParams,
    totalResults,
    setSelectedCodespaceId,
    selectedCodespaceId,
    fetchCodespaces
  } = useCodespacesWithParams()
  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedCodespaceId)

  const { data: singleCodespace, isFetching: isSingleCodespaceFetching } =
    useGetSingleCodespaceQuery(selectedCodespaceId ?? '', {
      skip: !selectedCodespaceId
    })

  const [deleteSingleCodespace, { isLoading: isDeleteLoading }] = useDeleteSingleCodespaceMutation()

  const breadcrumbs = [
    {
      title: t`Home`,
      link: CONTROL_DATA_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Codespaces`
    }
  ]

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({
      ...prevState,
      start_index: (newPage * ROWS_PER_PAGE_DEFAULT).toString()
    }))
  }

  function handleRequestSort(_event: React.MouseEvent<unknown, MouseEvent>, property: string) {
    const isAsc = params.sort_order === SORT_ORDER_ASC
    setParams(prevState => ({
      ...prevState,
      sort_by: property,
      sort_order: `${isAsc ? SORT_ORDER_DESC : SORT_ORDER_ASC}`
    }))
  }

  const onRowClick = (id: string) => {
    setSelectedCodespaceId(id)
    setIsDrawerOpen(true)
  }

  const handleDrawerClose = () => {
    setIsDrawerOpen(false)
    setSelectedCodespaceId('')
  }

  const handleDelete = () => {
    if (!selectedCodespaceId) return

    deleteSingleCodespace(selectedCodespaceId)
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Codespace successfully deleted.`)
        fetchCodespaces()
        handleDrawerClose()
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error deleting the codespace.`)
      })
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Breadcrumbs items={breadcrumbs} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>{t`Codespaces`}</Typography>
        <Box mt={4}>
          <ListActions>
            <CustomButton
              onClick={() => navigate(`${baseUrl}/create/`)}
              size="small"
              startIcon={<AddIcon />}
            >
              {t`Create new codespace`}
            </CustomButton>
          </ListActions>
          <Box pt={1}>
            <CodespaceList
              rows={codespaces}
              isLoading={isFetching ?? isDeleteLoading}
              onRowClick={onRowClick}
              selectedCodespaceId={selectedCodespaceId}
              params={params}
              handleRequestSort={handleRequestSort}
              handleChangePage={handleChangePage}
              count={totalResults}
              page={
                params.start_index ? Number.parseInt(params.start_index) / ROWS_PER_PAGE_DEFAULT : 0
              }
            />
          </Box>
          <DrawerRight
            open={isDrawerOpen}
            onClose={handleDrawerClose}
            topBar={
              <Box>
                <DeleteButton
                  sx={{ mr: 1 }}
                  size="small"
                  onClick={() =>
                    confirm({
                      title: t`Are you sure?`,
                      message: t`Are you sure you want to delete the codespace?`,
                      handleConfirm: () => {
                        handleDelete()
                      }
                    })
                  }
                >{t`Delete`}</DeleteButton>
                <CustomButton
                  onClick={() => navigate(`${baseUrl}/${selectedCodespaceId}/edit`)}
                  size="small"
                  startIcon={<EditIcon />}
                >{t`Edit`}</CustomButton>
              </Box>
            }
          >
            <WithLoader loading={isSingleCodespaceFetching || isDeleteLoading}>
              {singleCodespace && (
                <TabContext value={CodespaceTabType.DETAILS}>
                  <TabList aria-label={t`Details`}>
                    <Tab label={t`Details`} value={CodespaceTabType.DETAILS} />
                  </TabList>
                  <Divider />
                  <TabPanel sx={{ p: 0 }} value={CodespaceTabType.DETAILS}>
                    <CodespaceDetails codespace={singleCodespace} />
                  </TabPanel>
                </TabContext>
              )}
            </WithLoader>
          </DrawerRight>
        </Box>
      </Box>
    </Box>
  )
}
